import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";
import { Radio, Zap, Shield, Truck, Headphones, ChevronRight, Play, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  { id: "faq-iphone", q: "Do CreatorMic Pro microphones work with my iPhone?", a: "Yes — every product ships with USB-C, Lightning, and 3.5mm adapters so you can plug directly into your iPhone (Lightning or USB-C), iPad, Android phone, or camera without extra purchases." },
  { id: "faq-shipping", q: "What is the shipping time?", a: "Orders ship within 24 hours from our fulfillment center. Standard US delivery is 3-5 business days. Free shipping on all orders over $50." },
  { id: "faq-returns", q: "What is your return policy?", a: "30-day risk-free returns on all products. If you are not happy, return it for a full refund — we cover return shipping in the US." },
  { id: "faq-range", q: "How long is the range on your wireless mics?", a: "Solo Wireless: 40m line-of-sight. Pro Duo: 60m line-of-sight. Both include intelligent frequency hopping to maintain a stable connection in crowded RF environments." },
  { id: "faq-multi", q: "Can I use two mics at once?", a: "Yes — the CreatorMic Pro Wireless Duo is a dual-channel system with two transmitters and one receiver, perfect for interviews and podcasts." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 4 } }).then((r) => setFeatured(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "CreatorMic Pro",
    url: "https://creatormicpro.com",
    logo: "https://creatormicpro.com/logo.png",
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="fade-in" data-testid="page-home">
      <SEO
        title="Professional Wireless Audio for Every Creator"
        description="Wireless lavalier microphones, USB-C mics, Lightning mics, and mobile audio accessories for TikTok creators, YouTubers, podcasters, and mobile journalists."
        path="/"
        jsonLd={[orgSchema, faqSchema]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-32 grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <p className="eyebrow mb-6">CreatorMic Pro — 2026 Edition</p>
            <h1 className="text-h1 text-[#1D1D1F]">Professional wireless audio for every creator.</h1>
            <p className="mt-6 text-lg text-[#86868B] max-w-lg">Capture clear, reliable audio for videos, interviews, podcasts, livestreams, and mobile content — no adapters, no drama.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="hero-shop-btn">
                Shop CreatorMic Pro <ChevronRight size={16} />
              </Link>
              <a href="#how-it-works" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-how-it-works-btn">
                <Play size={14} /> See How It Works
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-[#86868B]">
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#0066FF]" /> Free US shipping over $50</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#0066FF]" /> 30-day returns</div>
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#0066FF]" /> 2-year warranty</div>
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-white shadow-[0_30px_80px_rgba(0,0,0,0.08)]">
              <img
                src="https://images.unsplash.com/photo-1630797160666-38e8c5ba44c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="Content creator recording with wireless mic"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 md:-left-10 bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.08)] px-5 py-4 border border-black/5">
              <p className="text-xs text-[#86868B]">Loved by</p>
              <p className="font-display text-2xl font-bold text-[#1D1D1F]">50,000+ creators</p>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 md:py-24 max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="eyebrow mb-3">Shop by category</p>
            <h2 className="text-h2 text-[#1D1D1F]">Find your perfect mic.</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-[#0066FF]" data-testid="home-shop-all">
            Shop all <ChevronRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          {cats.map((c) => (
            <Link
              key={c.slug}
              to={`/shop?category=${c.slug}`}
              data-testid={`category-${c.slug}`}
              className="soft-card group overflow-hidden aspect-[4/3] relative"
            >
              <img
                src={c.image}
                alt={c.image_alt || c.name}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white font-display font-bold text-lg leading-tight">{c.name}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-16 md:py-24 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="eyebrow mb-3">Featured</p>
              <h2 className="text-h2 text-[#1D1D1F]">Creator favorites.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* WHY CREATORS CHOOSE (BENTO) */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-14">
          <p className="eyebrow mb-3">Why creators choose CreatorMic Pro</p>
          <h2 className="text-h2 text-[#1D1D1F]">Studio-grade audio, without the studio.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 soft-card p-8 md:p-10 flex flex-col justify-between min-h-[320px]" data-testid="benefit-noise">
            <div>
              <Radio size={28} strokeWidth={1.5} className="text-[#0066FF]" />
              <h3 className="text-h3 mt-6 max-w-md">Intelligent noise cancellation trained on real creator scenes.</h3>
              <p className="text-[#86868B] mt-3 max-w-md">Traffic, cafes, wind, HVAC — our on-device DSP silences it while your voice comes through crisp and natural.</p>
            </div>
            <div className="mt-8 aspect-[3/1] rounded-2xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1673767298248-b128f17f89af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-5 soft-card p-8 md:p-10 min-h-[320px]" data-testid="benefit-battery">
            <Zap size={28} strokeWidth={1.5} className="text-[#0066FF]" />
            <h3 className="text-h3 mt-6">Shoot all day.</h3>
            <p className="text-[#86868B] mt-3">Up to 24 hours with the charge case. USB-C fast charge tops off a full session in 60 minutes.</p>
          </div>
          <div className="md:col-span-4 soft-card p-8 md:p-10 min-h-[280px]" data-testid="benefit-compat">
            <Headphones size={28} strokeWidth={1.5} className="text-[#0066FF]" />
            <h3 className="text-h3 mt-6">Works with everything.</h3>
            <p className="text-[#86868B] mt-3">iPhone, iPad, Android, Mac, PC, DSLR, mirrorless — every adapter, in the box.</p>
          </div>
          <div className="md:col-span-4 soft-card p-8 md:p-10 min-h-[280px]" data-testid="benefit-warranty">
            <Shield size={28} strokeWidth={1.5} className="text-[#0066FF]" />
            <h3 className="text-h3 mt-6">2-year warranty.</h3>
            <p className="text-[#86868B] mt-3">Backed by real humans in real time. Chat, email, or phone — we answer fast.</p>
          </div>
          <div className="md:col-span-4 soft-card p-8 md:p-10 min-h-[280px]" data-testid="benefit-shipping">
            <Truck size={28} strokeWidth={1.5} className="text-[#0066FF]" />
            <h3 className="text-h3 mt-6">Free US shipping.</h3>
            <p className="text-[#86868B] mt-3">Orders over $50 ship free. Every order is trackable, secure, and dispatched within 24 hours.</p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-20 md:py-28 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow mb-3">How it works</p>
            <h2 className="text-h2 text-[#1D1D1F]">Three steps. Zero setup pain.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", h: "Plug in the receiver", p: "USB-C, Lightning, or 3.5mm — connect the receiver to your phone or camera. No apps required." },
              { n: "02", h: "Clip the transmitter", p: "Magnetic clip attaches to any shirt. LEDs confirm sync and battery status in one glance." },
              { n: "03", h: "Hit record", p: "Broadcast-quality audio starts flowing instantly. 32-bit float safety recording is always on." },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-8 border border-black/5" data-testid={`step-${s.n}`}>
                <p className="font-mono-tech text-sm text-[#0066FF] mb-4">{s.n}</p>
                <h3 className="font-display text-xl font-bold text-[#1D1D1F] mb-2">{s.h}</h3>
                <p className="text-[#86868B]">{s.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="py-20 md:py-24 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center md:text-left">
          {[
            { n: "50,000+", l: "Creators shipped" },
            { n: "4.8/5", l: "Verified rating" },
            { n: "24h", l: "Order dispatch" },
            { n: "30 days", l: "Return window" },
          ].map((s) => (
            <div key={s.l} className="border-t border-black/10 pt-6">
              <p className="font-display text-4xl md:text-5xl font-bold text-[#1D1D1F]">{s.n}</p>
              <p className="text-sm text-[#86868B] mt-1">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="eyebrow mb-3">Frequently asked</p>
          <h2 className="text-h2 text-[#1D1D1F] mb-10">You have questions. We have answers.</h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="home-faq">
            {FAQS.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="bg-white rounded-2xl px-6 border border-black/5">
                <AccordionTrigger className="text-left font-display text-lg hover:no-underline" data-testid={`faq-q-${f.id}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#86868B]">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <Link to="/faq" className="btn-secondary inline-flex" data-testid="home-faq-more">See all FAQs</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-10">
        <div className="rounded-3xl bg-[#1D1D1F] text-white p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8" style={{ backgroundImage: "radial-gradient(1200px 300px at 100% 0%, rgba(0,102,255,0.35), transparent 60%)" }}>
          <div>
            <p className="eyebrow text-white/60 mb-3">Ready to record?</p>
            <h2 className="text-h2 max-w-xl">Level up your audio in one order.</h2>
          </div>
          <Link to="/shop" className="btn-primary" data-testid="home-cta-shop">Shop CreatorMic Pro</Link>
        </div>
      </section>
    </div>
  );
}
